import {
  ItineraryItem,
  Location,
  WaytoraExperienceInitial,
  ViatorProductInitial,
  AddingExperience,
} from "@/types";
import api from "@/config/axiosConfig";
import { DateTime } from "luxon";
import { getCoverImageSources } from "@tripbuilder/cat-experiences/experienceUtils";
import logger from "@/utils/logger";

export const calculateStartTime = async (
  existingExperiences: {
    date: string;
    items: ItineraryItem[];
  },
  timezone: string
): Promise<string> => {
  if (existingExperiences.items.length === 0) {
    const utcDate = DateTime.fromISO(existingExperiences.date, { zone: "UTC" });

    const localDefault = DateTime.fromObject(
      {
        year: utcDate.year,
        month: utcDate.month,
        day: utcDate.day,
        hour: 9,
        minute: 0,
        second: 0,
        millisecond: 0,
      },
      {
        zone: timezone,
      }
    );

    logger.log("Input date:", existingExperiences.date);
    logger.log("UTC date:", utcDate.toISO());
    logger.log("Local default:", localDefault.toISO());
    logger.log("Local default (UTC):", localDefault.toUTC().toISO());

    return localDefault.toUTC().toISO()!;
  }

  const lastExperience =
    existingExperiences.items[existingExperiences.items.length - 1];

  if (!lastExperience || !lastExperience.start_time) {
    throw new Error(
      "Last experience is undefined or does not have a start time"
    );
  }

  const lastStartTime = DateTime.fromISO(lastExperience.start_time, {
    zone: "UTC",
  }).setZone(timezone);

  const timeToSpendMinutes = lastExperience?.duration
    ? lastExperience.duration * 60
    : 120;

  let newStartTime = lastStartTime.plus({ minutes: timeToSpendMinutes });

  if (newStartTime.day !== lastStartTime.day) {
    console.warn("Calculated time crosses midnight, adjusting to 11:59 PM");
    newStartTime = lastStartTime.set({
      hour: 23,
      minute: 59,
      second: 59,
      millisecond: 999,
    });
  }

  const utcStartTime = newStartTime.toUTC().toISO()!;

  logger.log("New start time (local):", newStartTime.toISO());
  logger.log("New start time (UTC):", utcStartTime);
  logger.log("Last start time (local):", lastStartTime.toISO());
  logger.log("Time to spend (minutes):", timeToSpendMinutes);

  return utcStartTime;
};

export const getMainImage = (experienceData: any) => {
  if (!experienceData?.images) {
    return "/assets/imgloading.png";
  }
  const images = experienceData.images;
  const mainImage = images.find((image: any) => image?.main);
  if (mainImage?.url) {
    return mainImage.url;
  }
  if (images[0]?.url) {
    return images[0].url;
  }
  return "/assets/imgloading.png";
};

export const getAllImages = (experienceData: any) => {
  const images = experienceData.images;
  return images.map((image: any) => image.url);
};

export const getCoordinatesFromPlaceId = async (
  placeId: string,
  location: Location,
  name: string
): Promise<{
  address: string;
  latitude: number;
  longitude: number;
}> => {
  const response = await api.experiences.get("/general", {
    params: {
      place_id: placeId,
      name: name,
      city: location.name,
      country: location.address_components.country,
      latitude: location.coordinates.latitude,
      longitude: location.coordinates.longitude,
    },
  });
  return response.data;
};

export const convertToAddingExperience = (
  experience: WaytoraExperienceInitial | ViatorProductInitial
): AddingExperience => {
  if ("title" in experience) {
    // viator product
    const viatorProduct = experience as ViatorProductInitial;
    return {
      name: viatorProduct.title,
      image: getCoverImageSources(viatorProduct.images)?.highRes || "",
    };
  } else {
    // waytora experience
    const waytoraExperience = experience as WaytoraExperienceInitial;
    return {
      name: waytoraExperience.name,
      image: getMainImage(waytoraExperience),
    };
  }
};
